import ButtonMedium from 'components/common/Buttons/ButtonMedium';
import InputGeneral from 'components/common/FormControls/FormInputs';
import React, { useEffect, useState } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { getRedirectUrl } from 'utils/redirectUrlAfterLogin';
import {
  getDeviceIdCookie,
  loadAuthToken,
  saveDeviceIdCookie,
} from 'utils/tokenStorage';
import {
  loginSuccess,
  getUserProfileSuccess,
  ILoginSuccessPayload,
} from 'actions/authenticate';
import { Location, History } from 'history';
import { login } from 'services/authenticate';
import { MFA_STATE, MFA_ERROR_MSG } from 'utils/constants';
import { Redirect } from 'react-router-dom';
import { routes } from 'utils/routeHelper';
import './styles.scss';
import { AuthClientInstance } from 'api/authClientInstance';
import { VerifyMfaTokenRequestDTO } from '@peartherapeutics/auth-service-client';

const INVALID_TOKEN_STATUS = 401;

interface IOwnProps {
  location: Location<{ loginState: string; token: string; email: string }>;
  history: History;
  userLoggedIn: boolean;
  scopes: string[];
  loginAfterIdle: boolean;
}

interface IDispatchProps {
  loginSuccess: (payload: ILoginSuccessPayload) => void;
  getUserProfileSuccess: (profile: UserProfileDTO) => void;
}

type Props = IOwnProps & IDispatchProps;

const MFAValidation = (props: Props) => {
  const { userLoggedIn, loginAfterIdle, scopes } = props;
  const [verificationCode, setVerificationCode] = useState('');
  const [errMsg, setErrMsg] = useState('');

  useEffect(() => {
    const { state } = props.location;
    if (
      !state ||
      (state && (state.loginState !== MFA_STATE.MFA_VALIDATE || !state.token))
    ) {
      props.history.push(routes.LOG_IN);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      const mfaToken = props.location.state?.token || '';
      const email = props.location.state?.email || '';
      let deviceId = getDeviceIdCookie();
      if (!deviceId) {
        deviceId = saveDeviceIdCookie();
      }
      const verifyMfaTokenRequestDTO: VerifyMfaTokenRequestDTO = {
        deviceId,
        token: mfaToken,
        passcode: verificationCode,
      };
      const {
        data: { token, loginState },
      } = await AuthClientInstance.get().validateMfaToken({
        verifyMfaTokenRequestDTO,
      });

      if (loginState === MFA_STATE.LOGGED_IN) {
        const { profile } = await login(token);
        props.getUserProfileSuccess(profile);
        props.loginSuccess({ token, email });
      }
    } catch (error) {
      if (error.status === INVALID_TOKEN_STATUS) {
        window.location.replace(routes.LOG_IN);
        return;
      }
      setVerificationCode('');
      setErrMsg(MFA_ERROR_MSG);
    }
  };

  const handleChangeVerificationCode = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setVerificationCode(e.target.value);
  };

  const redirectAfterLoggedIn = (loginAfterIdle: boolean, scopes: string[]) => {
    const url = getRedirectUrl(scopes);
    if (loginAfterIdle) {
      window.location.pathname = url;
      return null;
    }
    return <Redirect to={url} />;
  };

  const isValid = verificationCode.length !== 0;

  return (
    <>
      {userLoggedIn && redirectAfterLoggedIn(loginAfterIdle, scopes)}
      <div data-testid="mfa-validation" styleName="wrapper">
        <div styleName="inside-wrapper">
          <div styleName="logo-pear" />
          <div styleName="instruction">
            Enter your verification code
            <br />
            using your authenticator app
          </div>
          <form
            onSubmit={handleSubmit}
            autoCorrect="off"
            styleName="submit-mfa-validation-code-form"
          >
            <InputGeneral
              error={!!errMsg}
              label="Verification code"
              name="verificationCode"
              testidProp="input-verification-code"
              value={verificationCode}
              type="login"
              onChange={handleChangeVerificationCode}
              helperText={errMsg}
              id="filled-error-helper-text"
            />
            <ButtonMedium
              buttonRole={'primary'}
              disabled={!isValid}
              buttonType="submit"
              width={'300px'}
              margin={'15px 0'}
            >
              Login
            </ButtonMedium>
          </form>
          <div styleName="instruction-footer">
            Can’t find your code?
            <br />
            Call PearConnect at 833-697-3738
          </div>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state: IRootStateType) => {
  return {
    userLoggedIn: Boolean(state.authenticate.token || loadAuthToken()),
    loginAfterIdle: state.authenticate.loginAfterIdle,
    roles: get(state, 'authenticate.profile.roles', []).map(
      (role: Role) => role.roleType,
    ),
    scopes: get(state, 'authenticate.profile.scopes', []),
  };
};

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      loginSuccess,
      getUserProfileSuccess,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(MFAValidation);
